import { skyFlightCapabilities } from '../../../packages/domain/src/106-sky-capabilities.mjs';

/** Visible relative-drag target. The flight input owner captures pointers and
 * owns the sight; this view only displays thumb travel and availability. */
export function createSkyAimPad({ e, surface }) {
  const element = e('button', 'sky-aim-pad');
  element.type = 'button';
  element.tabIndex = -1;
  element.hidden = true;
  element.setAttribute('aria-label', 'Aim pad. Drag to move crosshairs; lift to hold aim.');
  const thumb = e('span', 'sky-aim-thumb');
  thumb.setAttribute('aria-hidden', 'true');
  element.append(thumb, e('span', 'sky-steering-label', 'AIM'));
  surface.append(element);
  return {
    element,
    paint(enabled) {
      element.hidden = !enabled;
    },
    move(x, y) {
      const length = Math.max(28, Math.hypot(x, y));
      element.style.setProperty('--aim-x', String(x / length));
      element.style.setProperty('--aim-y', String(y / length));
      element.setAttribute('data-active', 'true');
    },
    clear() {
      element.style.setProperty('--aim-x', '0');
      element.style.setProperty('--aim-y', '0');
      element.setAttribute('data-active', 'false');
    },
  };
}

/** Independent movement pad throughout every flight phase. The canvas owns
 * the other pointer/sight; releasing either pointer cannot cancel the other.
 * Coordinates are normalized flight-frame axes, never camera/world positions.
 * Free flight adds explicitly selected, constant-axis cruise so two thumbs
 * remain available for movement and aim. It never selects a route or target.
 * Hidden, paused, protected and blurred controls clear cruise. */
export function createSkySteeringPad({
  e,
  surface,
  getState,
  canPlay,
  hasSequence,
  listen,
  focusCanvas,
  onPause = () => {},
  getSettings = () => ({}),
}) {
  const element = e('button', 'sky-steering-pad');
  const thumb = e('span', 'sky-steering-thumb');
  const label = e('span', 'sky-steering-label', 'MOVE');
  element.type = 'button';
  element.tabIndex = -1;
  element.setAttribute(
    'aria-label',
    'Flight steering pad. Drag to move; aim with your other finger.',
  );
  thumb.setAttribute('aria-hidden', 'true');
  element.append(thumb, label);
  element.hidden = true;
  const throttle = e('div', 'sky-throttle');
  throttle.setAttribute('role', 'group');
  throttle.setAttribute(
    'aria-label',
    'Flight cruise. Tap a direction to move; tap it again to stop.',
  );
  throttle.hidden = true;
  surface.append(element, throttle);
  const directions = [
    { name: 'Forward', value: 1 },
    { name: 'Back', value: -1 },
  ].map((direction) => {
    const button = e('button', 'sky-button sky-throttle-button', direction.name);
    button.type = 'button';
    button.setAttribute(
      'aria-label',
      `Start ${direction.name.toLowerCase()} cruise. Tap again to stop.`,
    );
    button.setAttribute('aria-pressed', 'false');
    throttle.append(button);
    return { ...direction, button };
  });
  let cruise = 0,
    pointer = null,
    axes = { x: 0, y: 0 };
  const capability = () =>
    skyFlightCapabilities(getState(), { ready: canPlay(), sequence: hasSequence() });
  const enabled = () => capability().manual.x || capability().manual.y;
  function showAxes() {
    element.style.setProperty('--steer-x', String(axes.x));
    element.style.setProperty('--steer-y', String(-axes.y));
  }
  const depthEnabled = () => enabled() && getState().freeFlight === true;
  function setCruise(value) {
    cruise = value;
    for (const { button, name, value: direction } of directions) {
      const active = cruise === direction;
      button.textContent = active ? `${name} · tap to stop` : name;
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute(
        'aria-label',
        active
          ? `${name} cruise active. Tap to stop.`
          : `Start ${name.toLowerCase()} cruise. Tap again to stop.`,
      );
    }
  }
  function clearPad() {
    const previous = pointer;
    pointer = null;
    axes = { x: 0, y: 0 };
    showAxes();
    if (previous !== null && element.hasPointerCapture(previous))
      element.releasePointerCapture(previous);
  }
  function clear() {
    setCruise(0);
    clearPad();
  }
  function move(event) {
    const rect = element.getBoundingClientRect();
    const radius = Math.max(1, Math.min(rect.width, rect.height) * 0.38);
    let x = (event.clientX - rect.left - rect.width / 2) / radius;
    let y = (rect.top + rect.height / 2 - event.clientY) / radius;
    const length = Math.hypot(x, y);
    if (length < 0.12) x = y = 0;
    else if (length > 1) {
      x /= length;
      y /= length;
    }
    const settings = getSettings(),
      gain = Number.isFinite(settings.moveSensitivity)
        ? Math.max(0.4, Math.min(2, settings.moveSensitivity))
        : 1;
    axes = {
      x: Math.max(-1, Math.min(1, x * gain * (settings.moveInvertX ? -1 : 1))),
      y: Math.max(-1, Math.min(1, y * gain * (settings.moveInvertY ? -1 : 1))),
    };
    showAxes();
  }
  listen(element, 'pointerdown', (event) => {
    if (!enabled() || event.button > 0) return;
    event.preventDefault();
    event.stopPropagation?.();
    if (pointer !== null) return;
    pointer = event.pointerId;
    focusCanvas();
    element.setPointerCapture(pointer);
    move(event);
  });
  listen(element, 'pointermove', (event) => {
    if (pointer !== event.pointerId) return;
    if (!enabled()) {
      clear();
      return;
    }
    event.preventDefault();
    event.stopPropagation?.();
    move(event);
  });
  for (const name of ['pointerup', 'pointercancel', 'lostpointercapture'])
    listen(element, name, (event) => {
      if (pointer === event.pointerId) clearPad();
    });
  listen(element, 'contextmenu', (event) => event.preventDefault());
  for (const { button, value } of directions) {
    listen(button, 'pointerdown', (event) => {
      if (!depthEnabled() || event.button > 0) return;
      // Keep the flight canvas focused; a second tap must stop the existing
      // cruise, not first clear it through an incidental canvas blur.
      event.preventDefault();
      event.stopPropagation?.();
      focusCanvas();
    });
    // Native buttons produce one click for touch, Enter, Space and assistive
    // activation. Do not toggle again in keydown or invent a held third touch.
    listen(button, 'click', (event) => {
      if (!depthEnabled() || event.button > 0) return;
      event.preventDefault();
      event.stopPropagation?.();
      setCruise(cruise === value ? 0 : value);
    });
    listen(button, 'keydown', (event) => {
      if (event.key === 'Escape' || event.key.toLowerCase() === 'p') {
        event.preventDefault();
        clear();
        onPause();
      }
    });
    listen(button, 'blur', () => setCruise(0));
    listen(button, 'contextmenu', (event) => event.preventDefault());
  }
  return {
    element,
    throttle,
    clear,
    paint() {
      const active = enabled();
      const cap = capability();
      element.hidden = !cap.aim;
      element.disabled = !active;
      label.textContent = active ? 'MOVE' : 'AUTO';
      element.setAttribute(
        'aria-label',
        active
          ? 'Flight steering pad. Drag to move; aim with your other finger.'
          : 'Autopilot controls movement. Your aiming control remains available.',
      );
      const depth = depthEnabled();
      throttle.hidden = !depth;
      for (const { button } of directions) button.disabled = !depth;
      if (!depth) setCruise(0);
      if (!active) clear();
    },
    sample() {
      if (!enabled()) {
        clear();
        return { x: 0, y: 0 };
      }
      if (getState().freeFlight !== true) return { ...axes };
      return { ...axes, forward: cruise };
    },
  };
}
